/**
 * AutoComplete
 *
 * @module components/auto-complete
 */

import Component from './component';

import AbortableAsync from '../libs/abortable-async';
import utils from '../utils';
import { getModule } from '../utils/modules';

const debounce = utils.debounce;

const EVENT_NAMESPACE = '.autocomplete';
const AUTOCOMPLETE_MAX_ITEMS = 5;
const AUTOCOMPLETE_ACTIVE_CLASS = '-active';
const AUTOCOMPLETE_MIN_LENGTH = 3;

export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  'use strict';

  /**
   * AutoComplete component class
   */
  class AutoComplete extends Component {
    /**
     * Constructor
     */
    constructor(element, options = {}) {
      super(element, options);
      this.name = 'auto-complete';

      this.selectors = $.extend(true, {}, AutoComplete.selectors, this.options.selectors || {});
      this.init();
    }

    /**
     * Main initializer
     * 
     * @returns void
     */
    init() {
      if (!AutoComplete.handlebars) {
        getModule('handlebars')
          .then(handlebars => {
            AutoComplete.handlebars = handlebars;
            this.init();
          })
          .catch(err => {
            this.logError(err);
          });

        return;
      }
      
      
      this._init();
    }

    /**
     * Initialize Auto Complete functionality.
     */
    _init() {
      this.tpls = {};

      try {
        this.tpls.autoCompleteList = this.element.find(this.selectors.autoCompleteListTemplate);
        if (this.tpls.autoCompleteList.length) {
          this.tpls.autoCompleteList = AutoComplete.handlebars.compile(this.tpls.autoCompleteList.first().html());
        }
        else {
          this.logError('AutoComplete List Template not defined.');
        }
      }
      catch (err) {
        this.logError(err);
        return;
      }

      this.autoCompleteContainer = this.element.find(this.selectors.autoCompleteContainer);
      this.autoCompleteListContainer = $(this.selectors.autoCompleteListContainer, this.autoCompleteContainer);
      
      this.autoCompleteState = {
        // original user input
        input: '',
        // autocomplete API async task instance
        request: null,
        // autocomplete predictions list
        predictions: [],
        // selected prediction
        selectedIndex: null
      };

      this.elInput = this.element.find(this.selectors.searchInput);

      this.elInputState = {
        // whether or not the input has focus
        hasFocus: false,
        // original user input
        input: this.elInput.val()
      };

      this.updateAutoCompleteDebounced = debounce(this.updateAutoComplete.bind(this), 200);

      this.elInput.off(EVENT_NAMESPACE);
      this.autoCompleteListContainer.off(EVENT_NAMESPACE);

      this.autoCompleteListContainer.on(`pointerdown${EVENT_NAMESPACE}`, this.selectors.autoCompleteItem, this._onAutoCompleteListItemClick.bind(this));
      this.elInput.on(`input${EVENT_NAMESPACE} focus${EVENT_NAMESPACE} blur${EVENT_NAMESPACE} keydown${EVENT_NAMESPACE}`, this._onAddressInput.bind(this));
    }

    /**
     * Auto Complete list item click handler.
     * 
     * @param {Event} ev 
     */
    _onAutoCompleteListItemClick(ev) {
      const t = $(ev.currentTarget);
      if (t.is('.-error, .-decoration')) {
        return;
      }

      this.highlightAutoCompletePredictions(this.autoCompleteItems.index(t));

      // trigger itemselect
      if (this.autoCompleteState.selectedIndex != null) {
        this.element.trigger('autocomplete-itemselect', this.autoCompleteState.predictions[this.autoCompleteState.selectedIndex]);
      }
    }

    /**
     * Address Input events handler.
     * 
     * @param {Event} ev 
     */
    _onAddressInput(ev) {
      const t = $(ev.currentTarget);

      switch (ev.type) {
        case 'focus':
          this.elInputState.hasFocus = true;
          this.updateAutoComplete();
          break;

        case 'blur':
          this.elInputState.hasFocus = false;
          this.hideElement(this.autoCompleteContainer);
          this.autoCompleteShown = false;
          break;

        case 'input':
          this.autoCompleteState.selectedIndex = null;
          if (!this.suppressAddressInputEvent) {
            this.elInputState.input = t.val();
            this.updateAutoCompleteDebounced();
          }
          break;

        case 'keydown':
          switch (ev.which) {
            // ARROW UP
            case 38:
              if (!this.autoCompleteShown) {
                return;
              }

              if (this.autoCompleteState.selectedIndex === null || this.autoCompleteState.selectedIndex < 0 || this.autoCompleteState.selectedIndex >= this.autoCompleteState.predictions.length) {
                this.highlightAutoCompletePredictions(this.autoCompleteState.predictions.length - 1);
              }
              else {
                this.highlightAutoCompletePredictions(this.autoCompleteState.selectedIndex - 1);
              }
              break;

            // ARROW DOWN
            case 40:
              if (!this.autoCompleteShown) {
                return;
              }

              if (this.autoCompleteState.selectedIndex === null || this.autoCompleteState.selectedIndex < 0 || this.autoCompleteState.selectedIndex >= this.autoCompleteState.predictions.length) {
                this.highlightAutoCompletePredictions(0);
              }
              else {
                this.highlightAutoCompletePredictions(this.autoCompleteState.selectedIndex + 1);
              }
              break;
          }
      }
    }

    /**
     * Select a prediction.
     * If a prediction is not selected yet and we have predictions, select first one.
     */
    select() {
      const userInput = this.elInputState.input;
      let selectedPrediction = (this.autoCompleteState.selectedIndex !== null) ? this.autoCompleteState.predictions[this.autoCompleteState.selectedIndex] : null;
      
      if (!userInput || (!selectedPrediction && this.autoCompleteState.predictions.length < 1)) {
        this.elInput[0].focus();
      }
      else if (!selectedPrediction && this.autoCompleteState.predictions.length) {
        this.highlightAutoCompletePredictions(0);
        selectedPrediction = this.autoCompleteState.predictions[this.autoCompleteState.selectedIndex];
      }

      if (selectedPrediction) {
        this.hideElement(this.autoCompleteContainer);
        this.autoCompleteShown = false;
        this.element.trigger('autocomplete-itemselect', selectedPrediction);
      }
      else {
        this.showAutoCompleteError();
      }
    }

    /**
     * Update autocomplete.
     * 
     * Determine one of the following cases:
     * - if input length is less then AUTOCOMPLETE_MIN_LENGTH, do nothing, end logic circuit.
     * - if user has selected a prediction and input has not changed, do nothing, end logic circuit.
     * - if input has focus
     *   - if autocomplete request for this user input has been done
     *     - if user has selected a prediction, do nothing, end logic circuit.
     *     - if user has not selected a prediction, show auto complete list, end logic circuit.
     *   - otherwise cancel current autocomplete request, make new one with latest user's input and then show the auto complete list.
     * 
     * @returns 
     */
    updateAutoComplete() {
      const userInput = this.elInputState.input;

      // check if already showing/making same query
      if (this.autoCompleteState.request && this.autoCompleteState.request.input === userInput) {
        this.updateAutoCompletePredictions();
        return;
      }

      // reset state
      this.autoCompleteState.input = this.elInputState.input;
      this.autoCompleteState.selectedIndex = null;

      // autoCompleteShown is used to determine if we should handle UP/DOWN arrow keypress.
      this.autoCompleteShown = false;

      // no user input or not enough characters, abort and hide autocomplete list
      if (!userInput || (AUTOCOMPLETE_MIN_LENGTH > 0 && userInput.length < AUTOCOMPLETE_MIN_LENGTH)) {
        this.abortAutoComplete();
        this.autoCompleteState.predictions = [];
        this.updateAutoCompletePredictions();
        return;
      }

      // make new autocomplete request
      this.requestAutoComplete()
        .then(predictions => {
          if (AUTOCOMPLETE_MAX_ITEMS && AUTOCOMPLETE_MAX_ITEMS > 0) {
            this.autoCompleteState.predictions = predictions.slice(0, AUTOCOMPLETE_MAX_ITEMS);
          }
          else {
            this.autoCompleteState.predictions.slice(0);
          }
        })
        .catch((...args) => {
          this.logError(args);
          this.autoCompleteState.predictions = [];
        })
        .finally(() => {
          this.updateAutoCompletePredictions();
        });
    }

    /**
     * Update AutoComplete list.
     * 
     * Determine if we shoud show error message or predictions list or hide the autocomplete.
     * 
     * @returns 
     */
    updateAutoCompletePredictions() {
      let showError = false;
      if (this.autoCompleteState.predictions.length < 1 && 
        this.autoCompleteState.input && 
        (AUTOCOMPLETE_MIN_LENGTH < 1 || (AUTOCOMPLETE_MIN_LENGTH > 0 && this.autoCompleteState.input.length >= AUTOCOMPLETE_MIN_LENGTH))) {
        // user has input, but no predictions was found, show error
        this.showAutoCompleteError();
        showError = true;
      }
      else {
        // have predictions
        this.autoCompleteListContainer.html(this.tpls.autoCompleteList(this.autoCompleteState.predictions));
      }

      // cache items
      this.autoCompleteItems = this.autoCompleteListContainer.find(this.selectors.autoCompleteItem);


      if (this.elInputState.hasFocus && this.autoCompleteState.predictions.length) {
        if (this.autoCompleteState.selectedIndex === null) {
          // we have predictions and user input has focus and hasn't select a prediction, show the predictions
          this.showElement(this.autoCompleteContainer);
          this.autoCompleteShown = true;
          return;
        }
      }

      if (!showError) {
        this.hideElement(this.autoCompleteContainer);
      }
      
      // autoCompleteShown is used to determine if we should handle UP/DOWN arrow keypress, so it needs to be call even tho
      //   the autocomplete is visually visible when showing the error.
      this.autoCompleteShown = false;
    }

    /**
     * Highlight a prediction.
     * 
     * If prediction is highlighted, show it on address input, otherwise revert to original user input.
     * 
     * @param {Int} idx 
     */
    highlightAutoCompletePredictions(idx) {
      if (idx < 0 || idx >= this.autoCompleteState.predictions.length) {
        idx = null;
      }

      this.autoCompleteState.selectedIndex = idx;
      this.autoCompleteItems.removeClass(AUTOCOMPLETE_ACTIVE_CLASS);

      if (idx !== null) {
        this.autoCompleteItems.eq(idx).addClass(AUTOCOMPLETE_ACTIVE_CLASS);
        this.suppressAddressInputEvent = true;
        this.elInput.val(this.autoCompleteState.predictions[idx].description);
        this.suppressAddressInputEvent = false;
      }
      else {
        this.elInput.val(this.autoCompleteState.input);
      }
    }

    /**
     * Cancel autocomplete request.
     */
    abortAutoComplete() {
      if (this.autoCompleteState.request) {
        this.autoCompleteState.request.abort();
        this.autoCompleteState.request = null;
      }
    }

    /**
     * Show autocomplete with error message.
     */
    showAutoCompleteError() {
      this.autoCompleteListContainer.html(this.tpls.autoCompleteList({
        error: true
      }));
      this.showElement(this.autoCompleteContainer);
    }

    /**
     * Start autocomplete request.
     * 
     * @param {String} keyword 
     * @returns 
     */
    requestAutoComplete(keyword) {
      keyword = (keyword === undefined ? this.autoCompleteState.input : keyword);

      this.abortAutoComplete();

      const req = new AbortableAsync();
      this.autoCompleteState.request = req;
      req.input = keyword;

      req
        .run(() => {
          if (typeof this.options.onRequestAutoComplete !== 'function') {
            req.reject('AutoComplete onRequestAutoComplete callback is not defined.');
          }
          else {
            this.options.onRequestAutoComplete.apply(this, [keyword, req.resolve.bind(req), req.reject.bind(req)]);
          }
        });

      return req;
    }

    /**
     * Hide specific element.
     * 
     * @param {jQuery} el 
     * @param {String} [showClass]
     * @param {String} [hideClass]
     */
    hideElement(el, showClass, hideClass) {
      el.hide().attr('aria-hidden', 'true').removeClass(showClass || '').addClass(hideClass || '');
    }

    /**
     * Show specific element.
     * 
     * @param {jQuery} el 
     * @param {String} [showClass]
     * @param {String} [hideClass]
     */
    showElement(el, showClass, hideClass) {
      el.show().attr('aria-hidden', 'false').removeClass(hideClass || '').addClass(showClass || '');
    }
  }

  AutoComplete.selectors = {
    autoCompleteContainer: '.autocomplete--container',
    autoCompleteListContainer: '.autocomplete--items',
    autoCompleteItem: '.autocomplete--item',
    autoCompleteListTemplate: '[data-language-and-location--tpl="autocomplete-list"]',
    searchInput: '.autocomplete--input'
  };

  return AutoComplete;
})(jQuery, window);